import { useRouter } from 'next/router'
import { Box } from '@machobear/design-system'
import noop from 'lodash/noop'
import { Marker as MaplibreMarker } from 'react-map-gl/maplibre'

import CurrencyFormatter from '@/components/currency-formatter'

import * as styles from './marker.css'

type Props = {
  position: [number, number]
  properties?: { count: number; price: number; is_group: boolean }
  buildingId?: string
  onClick?: (position: [number, number], map: any, properties: any) => void
  children?: React.ReactNode
  IconComponent?: React.ComponentType<any>
  hidePrice?: boolean
  visited?: boolean
  showHoverEffect?: boolean
  style?: React.CSSProperties
}

interface IconProps {
  price: string | JSX.Element
  count: string
  showHoverEffect?: boolean
  isSingle?: boolean
  visited?: boolean
}

const DefaultIcon: React.FC<IconProps> = ({
  price,
  count,
  isSingle,
  visited,
  showHoverEffect,
  ...rest
}) => {
  const countInt = parseInt(count, 10)
  const width = countInt / 150 + 25 + count.toString().length * 2
  const height = width
  const fontSize = countInt / 500 + 12 + 'px'

  return (
    <Box
      className={[
        styles.icon,
        isSingle ? styles.single : styles.cluster,
        showHoverEffect && styles.hoverEvent[isSingle ? 'single' : 'cluster'],
        isSingle && visited ? styles.visited : ''
      ]}
      style={{
        width: isSingle ? undefined : width,
        height: isSingle ? undefined : height,
        fontSize
      }}
      {...rest}
    >
      <span className={styles.content}>{isSingle ? price : count}</span>
    </Box>
  )
}

const Marker = ({
  children,
  position,
  properties,
  buildingId: building_id,
  onClick = noop,
  showHoverEffect = false,
  hidePrice = false,
  visited,
  IconComponent = DefaultIcon,
  ...rest
}: Props) => {
  const map = null // do we need instance of map?

  const isGroup = properties?.is_group
  const isSingle = Number(properties?.count) === 1
  const router = useRouter()
  const locale = router?.locale

  const formattedPrice = hidePrice ? (
    <>$****</>
  ) : (
    <>
      <CurrencyFormatter value={properties?.price} locale={locale} />
      {isGroup && '+'}
    </>
  )

  return (
    <MaplibreMarker
      longitude={position[1]}
      latitude={position[0]}
      onClick={() => onClick(position, map, { ...properties, building_id })}
      offset={isSingle ? [0, -15] : undefined}
      {...rest}
    >
      <IconComponent
        showHoverEffect={showHoverEffect}
        price={formattedPrice}
        count={properties?.count}
        isSingle={isSingle}
        visited={visited}
      />
      {children}
    </MaplibreMarker>
  )
}

export default Marker
